import { computed } from "vue";

import { useNewsletter } from "@/store/stores/account";

const isoWeekStartsOn = 1; // Monday

const systemLanguage = window.navigator.language;
export const systemTimezone = new Intl.DateTimeFormat().resolvedOptions()
  .timeZone;

const effectiveLanguage = computed(() => {
  const newsletter = useNewsletter();
  const lang = newsletter.value.locale;

  if (!lang) {
    return systemLanguage;
  }

  if (lang === "en") {
    return "en-US";
  }

  return lang;
});

const dateFormatters = computed(() => {
  const locale = effectiveLanguage.value;
  return {
    // ISO formats (sv-SE gives YYYY-MM-DD / HH:MM:SS)
    isoDate: new Intl.DateTimeFormat("sv-SE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }),
    isoTime: new Intl.DateTimeFormat("sv-SE", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
    // Locale-aware formats
    time: new Intl.DateTimeFormat(locale, {
      timeStyle: "short",
    }),
    date: new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }),
    dateTime: new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
    weekdayLong: new Intl.DateTimeFormat(locale, {
      weekday: "long",
    }),
    // DateInput/DateCalendar formats
    dateParts: new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
    monthLong: new Intl.DateTimeFormat(locale, {
      month: "long",
    }),
    monthShort: new Intl.DateTimeFormat(locale, {
      month: "short",
    }),
    monthYear: new Intl.DateTimeFormat(locale, {
      month: "long",
      year: "numeric",
    }),
    weekdayShort: new Intl.DateTimeFormat(locale, {
      weekday: "short",
    }),
    fullDate: new Intl.DateTimeFormat(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
  };
});

const coerceDate = (date: Date | string | number): Date | number => {
  if (typeof date === "string") {
    return new Date(date);
  }

  return date;
};

const ISO_DATE_RE = /(^\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export const isValidISODate = (input: unknown): input is string => {
  return typeof input === "string" && ISO_DATE_RE.test(input);
};

export const parseISODate = (iso: string): Date | null => {
  const match = ISO_DATE_RE.exec(iso);

  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  return new Date(+year, +month - 1, +day);
};

export const formatISODate = (date: Date | string | number): string => {
  return dateFormatters.value.isoDate.format(coerceDate(date));
};

export const formatISOTime = (date: Date | string | number): string => {
  return dateFormatters.value.isoTime.format(coerceDate(date));
};

export const getLocaleDateParts = (): Intl.DateTimeFormatPart[] => {
  const sampleDate = new Date(2000, 0, 15);
  return dateFormatters.value.dateParts.formatToParts(sampleDate);
};

export const formatMonthName = (month: number): string => {
  return dateFormatters.value.monthLong.format(new Date(2000, month - 1, 1));
};

export const formatMonthShort = (month: number): string => {
  return dateFormatters.value.monthShort.format(new Date(2000, month - 1, 1));
};

export const formatMonthYear = (date: Date | string | number): string => {
  return dateFormatters.value.monthYear.format(coerceDate(date));
};

export const formatWeekdayShort = (date: Date | string | number): string => {
  return dateFormatters.value.weekdayShort.format(coerceDate(date));
};

export const formatFullDate = (date: Date | string | number): string => {
  return dateFormatters.value.fullDate.format(coerceDate(date));
};

export const formatTime = (date: Date | string | number): string => {
  return dateFormatters.value.time.format(coerceDate(date));
};

export const formatDateNumeric = (date: Date | string | number): string => {
  return dateFormatters.value.date.format(coerceDate(date));
};

export const formatDateTimeNumeric = (date: Date | string | number): string => {
  return dateFormatters.value.dateTime.format(coerceDate(date));
};

export const formatDayOfWeek = (date: Date | string | number): string => {
  return dateFormatters.value.weekdayLong.format(coerceDate(date));
};

export const formatRelativeTime = (
  a: Date | string | number,
  b: Date | string | number = Date.now(),
  display: "long" | "narrow" | "short" = "long"
): string => {
  const SECOND = 1e3;
  const MINUTE = SECOND * 60;
  const HOUR = MINUTE * 60;
  const DAY = HOUR * 24;
  const MONTH = DAY * 30;
  const YEAR = DAY * 365.25;

  const delta = Math.abs(new Date(b).getTime() - new Date(a).getTime());

  let value: number;
  let unit: Intl.RelativeTimeFormatUnit;

  if (delta < MINUTE) {
    if (display !== "long") {
      return `now`;
    }

    return `less than a minute`;
  } else if (delta < HOUR) {
    value = Math.floor(delta / MINUTE);
    unit = "minute";
  } else if (delta < DAY) {
    value = Math.round(delta / HOUR);
    unit = "hour";
  } else if (delta < MONTH) {
    // Use rounding, this handles the following scenario:
    // - 2024-02-13T09:00Z <- 2024-02-15T07:00Z = 2d
    value = Math.round(delta / DAY);
    unit = "day";
  } else if (delta < YEAR) {
    value = Math.floor(delta / MONTH);
    unit = "month";
  } else {
    value = Math.floor(delta / YEAR);
    unit = "year";
  }

  const formatter = new Intl.NumberFormat("en-US", {
    style: "unit",
    unit: unit,
    unitDisplay: display,
  });

  return formatter.format(value);
};

// Day utilities
export const isSameDay = (a: Date, b: Date): boolean => {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

export const addDays = (date: Date | string | number, amount: number): Date => {
  const cloned = new Date(date);
  cloned.setDate(cloned.getDate() + amount);

  return cloned;
};

export const endOfDay = (date: Date | string | number): Date => {
  const cloned = new Date(date);
  cloned.setHours(23, 59, 59, 999);

  return cloned;
};

// Week utilities
export const startOfWeek = (date: Date | string | number): Date => {
  const cloned = new Date(date);
  const day = cloned.getDay();
  cloned.setDate(cloned.getDate() - day);
  cloned.setHours(0, 0, 0, 0);
  return cloned;
};

export const startOfISOWeek = (date: Date | string | number): Date => {
  const cloned = new Date(date);

  const day = cloned.getDay();
  const diff = (day < isoWeekStartsOn ? 7 : 0) + day - isoWeekStartsOn;

  cloned.setDate(cloned.getDate() - diff);
  cloned.setHours(0, 0, 0, 0);

  return cloned;
};

export const endOfISOWeek = (date: Date | string | number): Date => {
  const cloned = new Date(date);

  const day = cloned.getDay();
  const diff = (day < isoWeekStartsOn ? -7 : 0) + 6 - (day - isoWeekStartsOn);

  cloned.setDate(cloned.getDate() + diff);
  cloned.setHours(23, 59, 59, 999);
  return cloned;
};

// Month utilities
export const addMonths = (
  date: Date | string | number,
  amount: number
): Date => {
  const cloned = new Date(date);
  const dayOfMonth = cloned.getDate();

  const endOfDesiredMonth = new Date(cloned);
  endOfDesiredMonth.setMonth(cloned.getMonth() + amount + 1, 0);

  const daysInMonth = endOfDesiredMonth.getDate();

  if (dayOfMonth >= daysInMonth) {
    return endOfDesiredMonth;
  }

  cloned.setFullYear(
    endOfDesiredMonth.getFullYear(),
    endOfDesiredMonth.getMonth(),
    dayOfMonth
  );

  return cloned;
};

export const startOfMonth = (date: Date | string | number): Date => {
  const cloned = new Date(date);
  cloned.setDate(1);
  cloned.setHours(0, 0, 0, 0);

  return cloned;
};

export const endOfMonth = (date: Date | string | number): Date => {
  const cloned = new Date(date);
  cloned.setFullYear(cloned.getFullYear(), cloned.getMonth() + 1, 0);
  cloned.setHours(23, 59, 59, 999);

  return cloned;
};

// Year utilities
export const addYears = (date: Date | string | number, amount: number) => {
  const cloned = new Date(date);
  cloned.setFullYear(cloned.getFullYear() + amount);

  return cloned;
};

export const startOfYear = (date: Date | string | number): Date => {
  const cloned = new Date(date);
  cloned.setFullYear(cloned.getFullYear(), 0, 1);
  cloned.setHours(0, 0, 0, 0);

  return cloned;
};

export const endOfYear = (date: Date | string | number): Date => {
  return new Date(new Date(date).getFullYear(), 11, 31);
};
